import { action, observable, reaction, computed, extendObservable, toJS } from 'mobx';
import { debounce as _debounce } from 'lodash';
import * as dateParse from 'date-fns/parse';
import {
    IAnyUser,
    IUsersListFromApi,
    EUserTypeFilter,
    EUserStatusFiler,
    ICustomersListFromApi,
    ICustomerFromApi
} from '../../../common/interfaces/users';
import { app } from '../App';
import { StringMethod } from '../../../common/helpers/string';

export class Users {
    constructor() {
        this.debouncedUsersFetch = _debounce(this.debouncedUsersFetch, 300);
    }

    // User modal
    @observable public userModalIsOpen: boolean = false;
    @observable public userModalUserId: number | null = null;

    // Get details of user in modal
    @computed
    public get userModalUserDetails(): IAnyUser | null {
        if (this.userModalUserId !== null && this.userModalUserId) {
            let userFound = -1;
            this.users.some((user, userIndex) => {
                if (user.id === this.userModalUserId) {
                    userFound = userIndex;
                    return true;
                }
            });

            return userFound !== -1 ? this.users[userFound] : null;
        } else {
            return null;
        }
    }

    // Users listing
    @observable public loading: boolean = false;
    @observable public users: IAnyUser[] = [];
    @observable public usersSearch: string = '';
    @observable public usersType: EUserTypeFilter = EUserTypeFilter.All;
    @observable public usersStatus: EUserStatusFiler = EUserStatusFiler.All;
    @observable public usersTotal: number = 0;
    @observable public usersPage: number = 1;
    public usersPerPage: number = 50;

    // React to search value change
    private reactToSearchChange = reaction(
        () => this.usersSearch,
        searchQuery => {
            if (searchQuery.trim().length > 0) {
                this.debouncedUsersFetch(true);
            } else {
                this.fetchUsers(true);
            }
        }
    );

    // React to user type filter change
    private reactToUsersTypeFilterChange = reaction(() => this.usersType, async type => await this.fetchUsers(true));

    // React to user status filter change
    private reactToUsersStatusFilterChange = reaction(() => this.usersStatus, async status => await this.fetchUsers(true));

    // Change the page
    @action
    public changeUsersPage = (page: number) => {
        this.usersPage = page > 0 ? page : 1;
        this.fetchUsers();
    };

    // Open user modal
    @action
    public openUserModal = (userId: number | null = null) => {
        this.userModalUserId = userId;
        this.userModalIsOpen = true;
        this.customerCardCodeSuggestions = [];
        this.customerCardCodeSuggestionsLoading = false;
        this.customerCardCodeSuggestionsTotal = 0;
        this.customerCardCodeSuggestionsLastFetchSkip = 0;
    };

    // Close user modal
    @action
    public closeUserModal = () => {
        this.userModalIsOpen = false;
    };

    // Fetch users after deboucne
    public debouncedUsersFetch = (fromFirstPage: boolean = false) => {
        this.fetchUsers(fromFirstPage);
    };

    // Fetch users
    @action
    public fetchUsers = async (fromFirstPage: boolean = false): Promise<boolean> => {
        try {
            this.loading = true;

            if (fromFirstPage) {
                this.usersPage = 1;
            }

            const service = await app.service('users');
            const users = (await service.find({
                query: {
                    ...(this.usersStatus !== EUserStatusFiler.All
                        ? {
                              status: this.usersStatus === EUserStatusFiler.Active ? 1 : 0
                          }
                        : {}),
                    ...(this.usersType !== EUserTypeFilter.All
                        ? {
                              typeId:
                                  this.usersType === EUserTypeFilter.Admin
                                      ? 1
                                      : this.usersType === EUserTypeFilter.Portal
                                          ? 2
                                          : this.usersType === EUserTypeFilter.Internal
                                              ? 3
                                              : 0
                          }
                        : {}),
                    ...(this.usersSearch.trim().length > 0
                        ? {
                              search: this.usersSearch.trim().toLowerCase()
                          }
                        : {}),
                    ...{
                        $limit: this.usersPerPage,
                        $skip: this.usersPerPage * this.usersPage - this.usersPerPage
                    }
                }
            })) as IUsersListFromApi;

            this.usersTotal = users.total;
            this.users = users.data.map(user => ({
                id: user.id,
                username: user.username,
                email: user.email,
                emailConfirmed: user.confirmEmail ? true : false,
                name: user.name,
                isCustomer: user.typeId === 2,
                isAdmin: user.typeId === 1,
                isInternal: user.typeId === 3,
                typeId: user.typeId,
                cardcode: user.cardcode,
                slpcode: user.slpcode,
                portalPermissionId: user.permissionId,
                isActive: user.status ? true : false,
                lastLoggedAt: user.lastLoginAt ? dateParse(user.lastLoginAt) : null,
                updatedAt: dateParse(user.updatedAt),
                createdAt: dateParse(user.cardcode)
            }));

            this.loading = false;

            return true;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    // Reset user's password
    @action
    public resetUserPassword = async (userId: number): Promise<boolean> => {
        try {
            const service = await app.service('users');
            const patch = await service.patch(userId, {
                generatePassword: true,
                sendConfirmationEmail: true
            });

            return true;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    // Update user's details
    @action
    public updateUserDetails = async (user: IAnyUser): Promise<boolean> => {
        try {
            if (user.id !== null) {
                const emailHasChanged =
                    this.userModalUserDetails === null || user.email.trim() !== this.userModalUserDetails.email.trim();

                const formattedCardcode = StringMethod.removeFromBeginning(user.cardcode.trim(), 'C');

                const service = await app.service('users');
                const patch = (await service.patch(user.id, {
                    username: user.username.trim(),
                    name: user.name.trim(),
                    cardcode: formattedCardcode,
                    confirmEmail: emailHasChanged ? 0 : this.userModalUserDetails.emailConfirmed ? 1 : 0,
                    sendConfirmationEmail: emailHasChanged ? 1 : 0,
                    typeId: user.typeId,
                    permissionId: user.portalPermissionId,
                    status: user.isActive ? 1 : 0,
                    ...(emailHasChanged
                        ? {
                              email: user.email.trim()
                          }
                        : {})
                })) as IAnyUser;

                this.users.some((u, uIndex) => {
                    if (u.id === user.id) {
                        extendObservable(this.users[uIndex], toJS(user));
                        return true;
                    }
                });
            }

            return true;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    // Create new user
    @action
    public createNewUser = async (user: IAnyUser, password: string): Promise<boolean> => {
        try {
            const formattedCardcode = StringMethod.removeFromBeginning(user.cardcode.trim(), 'C');

            const service = await app.service('users');
            const create = (await service.create({
                username: user.username.trim(),
                name: user.name.trim(),
                cardcode: formattedCardcode,
                email: user.email.trim(),
                password,
                typeId: user.typeId,
                permissionId: user.portalPermissionId,
                status: user.isActive ? 1 : 0,
                sendConfirmationEmail: 1
            })) as IAnyUser;

            this.users = [
                ...[
                    {
                        ...user,
                        id: create.id
                    }
                ],
                ...this.users
            ];

            return true;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    // Customers card code
    @observable public customerCardCodeSuggestions: ICustomerFromApi[] = [];
    @observable public customerCardCodeSuggestionsLoading: boolean = false;
    @observable public customerCardCodeSuggestionsTotal: number = 0;
    @observable public customerCardCodeSuggestionsLastFetchSkip: number = 0;
    public customerCardCodeSuggestionsLimit: number = 10;

    // Fetch customers by card code
    @action
    public getCustomers = async (query: string, loadNext: boolean = false): Promise<boolean> => {
        try {
            this.customerCardCodeSuggestionsLoading = true;

            this.customerCardCodeSuggestionsLastFetchSkip = loadNext
                ? this.customerCardCodeSuggestionsLastFetchSkip + this.customerCardCodeSuggestionsLimit
                : 0;

            const service = await app.service('customer');
            const suggestions = (await service.find({
                query: {
                    $or: [{ cardcode: { $like: query + '%' } }, { cardname: { $like: '%' + query + '%' } }],
                    $skip: this.customerCardCodeSuggestionsLastFetchSkip,
                    $limit: this.customerCardCodeSuggestionsLimit
                }
            })) as ICustomersListFromApi;

            this.customerCardCodeSuggestionsTotal = suggestions.total;
            this.customerCardCodeSuggestions = suggestions.data;
            this.customerCardCodeSuggestionsLoading = false;

            return true;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/web/src/stores/Users.ts